import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { NodeToolbar as NodeToolbarRaw, Position, useReactFlow, useStore } from '@xyflow/react';
import { useI18n } from '@/providers/i18n';
import { Copy, Trash2, Pencil, Sparkles, ClipboardPaste, Scissors, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { textToTiptapJSON } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import type { PromptAssetItem } from '@/components/nodes/shared/use-prompt-summon';
import { selectNodeSelectedById } from '../store-selectors';
import { useIsMultiSelect } from '@/components/canvas/utils/multi-select-store';

type TextTopToolbarProps = {
  nodeId: string;
  text?: string;
  isToolbarBlocked?: boolean;
  showPrompt?: boolean;
  canEdit?: boolean;
  onOpenEditor: () => void;
  onPaste?: (text: string) => void;
  onSplit?: (mode: 'paragraph' | 'line' | 'sentence' | 'comma') => void;
  onClear?: () => void;
};

export function TextTopToolbar({
  nodeId,
  text,
  isToolbarBlocked,
  showPrompt = true,
  canEdit = true,
  onOpenEditor,
  onPaste,
  onSplit,
  onClear,
}: TextTopToolbarProps) {
  const { t } = useI18n();
  const { updateNodeData } = useReactFlow();
  const selectIsSelected = useMemo(
    () => selectNodeSelectedById(nodeId),
    [nodeId]
  );
  const isSelected = useStore(selectIsSelected);
  const isMultiSelect = useIsMultiSelect();

  const [topToolbarShown, setTopToolbarShown] = useState(false);
  const [topToolbarPointerEnabled, setTopToolbarPointerEnabled] = useState(true);
  const topToolbarPointerTimer = useRef<number | null>(null);

  // Templates dropdown: integrates prompt-asset search (similar to usePromptSummon)
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateQuery, setTemplateQuery] = useState('');
  const [debouncedTemplateQuery, setDebouncedTemplateQuery] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateMyItems, setTemplateMyItems] = useState<PromptAssetItem[]>([]);
  const [templatePublicItems, setTemplatePublicItems] = useState<PromptAssetItem[]>([]);
  const [templateSearchFocused, setTemplateSearchFocused] = useState(false);

  useEffect(() => {
    if (isSelected && !isMultiSelect) {
      setTopToolbarShown(true);
      setTopToolbarPointerEnabled(false);
      if (topToolbarPointerTimer.current) window.clearTimeout(topToolbarPointerTimer.current);
      topToolbarPointerTimer.current = window.setTimeout(() => setTopToolbarPointerEnabled(true), 150);
    } else {
      setTopToolbarShown(false);
      setTopToolbarPointerEnabled(true);
      if (topToolbarPointerTimer.current) window.clearTimeout(topToolbarPointerTimer.current);
    }
    return () => {
      if (topToolbarPointerTimer.current) window.clearTimeout(topToolbarPointerTimer.current);
    };
  }, [isSelected, isMultiSelect]);

  // Reset templates search state when closing.
  useEffect(() => {
    if (templatesOpen) return;
    setTemplateQuery('');
    setDebouncedTemplateQuery('');
    setTemplateLoading(false);
    setTemplateMyItems([]);
    setTemplatePublicItems([]);
    setTemplateSearchFocused(false);
  }, [templatesOpen]);

  // Debounce query to reduce request pressure.
  useEffect(() => {
    if (!templatesOpen) {
      setDebouncedTemplateQuery('');
      return;
    }
    const raw = String(templateQuery || '');
    if (!raw.trim()) {
      setDebouncedTemplateQuery('');
      return;
    }
    const tmr = window.setTimeout(() => setDebouncedTemplateQuery(raw), 200);
    return () => {
      try { window.clearTimeout(tmr); } catch {}
    };
  }, [templatesOpen, templateQuery]);

  // Fetch prompt assets (my + public) when searching.
  useEffect(() => {
    if (!templatesOpen) return;
    const q = String(debouncedTemplateQuery || '').trim();
    // Avoid loading the whole library without a query.
    if (!q) {
      setTemplateLoading(false);
      setTemplateMyItems([]);
      setTemplatePublicItems([]);
      return;
    }

    const ac = new AbortController();
    setTemplateLoading(true);

    const fetchScope = async (scope: 'my' | 'public') => {
      const params = new URLSearchParams();
      params.set('scope', scope);
      params.set('page', '1');
      params.set('perPage', '20');
      params.set('order', 'time_desc');
      params.set('appliesTo', 'all');
      params.set('q', q);
      const res = await fetch(`/api/prompt-assets?${params.toString()}`, { signal: ac.signal });
      const data = await res.json();
      if (!res.ok || data?.error) throw new Error(data?.error || 'FAILED');
      const items = Array.isArray(data?.items) ? data.items : [];
      return items as PromptAssetItem[];
    };

    Promise.allSettled([fetchScope('my'), fetchScope('public')])
      .then((results) => {
        const my = results[0].status === 'fulfilled' ? results[0].value : [];
        const pub = results[1].status === 'fulfilled' ? results[1].value : [];
        setTemplateMyItems(my);
        setTemplatePublicItems(pub);
      })
      .catch(() => {})
      .finally(() => setTemplateLoading(false));

    return () => {
      try { ac.abort(); } catch {}
    };
  }, [templatesOpen, debouncedTemplateQuery]);

  const handleCopy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(t('Copied'));
  };

  const handlePasteAction = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (!clip) return;
      if (onPaste) {
        onPaste(clip);
      } else {
        const nextText = text ? `${text}${text.endsWith('\n') ? '' : '\n'}${clip}` : clip;
        updateNodeData(nodeId, { 
          content: textToTiptapJSON(nextText), 
          text: nextText 
        });
      }
      toast.success(t('Pasted'));
    } catch {
      toast.error(t('Failed to paste'));
    }
  };

  const handleClearAction = () => {
    if (onClear) {
      onClear();
    } else {
      updateNodeData(nodeId, { content: textToTiptapJSON(''), text: '' });
    }
    toast.success(t('Cleared'));
  };

  const applyTemplate = (template: { id: string; label: string; content: string }) => {
    updateNodeData(nodeId, {
      content: textToTiptapJSON(template.content),
      text: template.content,
      // keep for downstream features (e.g. split metadata), but now sourced from prompt-assets (not built-in)
      promptTemplate: template,
    });
    toast.success(t('Prompt applied'));
  };

  const handleSelectPromptAsset = (item: PromptAssetItem) => {
    applyTemplate({ id: item.id, label: item.title, content: item.prompt });
  };

  const toolbarButtons = (
    <div className="fm-toolbar-surface flex flex-col gap-1 p-1 rounded-xl bg-sidebar/95 backdrop-blur-xl border border-sidebar-border shadow-none">
      <div className="flex items-center gap-1">
        {/* Keep fixed button sizes so i18n labels don’t cause layout shifts */}
        {/* Prompt */}
        {showPrompt ? (
          <DropdownMenu open={templatesOpen} onOpenChange={setTemplatesOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-[96px] px-2.5 text-[11px] rounded-lg justify-center gap-1.5 hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group"
                disabled={isToolbarBlocked}
              >
                <Sparkles
                  size={14}
                  className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity text-primary"
                />
                <span className="truncate">{t('Prompt')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border/50 shadow-2xl rounded-xl w-[340px] p-1.5 z-[200]"
              onCloseAutoFocus={(e) => {
                // Prevent focus jumping back to canvas and breaking shortcuts.
                e.preventDefault();
              }}
            >
              {/* Keep the search header and empty/loading area the same height */}
              <div className="h-[46px] flex items-stretch">
                <Input
                  value={templateQuery}
                  onChange={(e) => setTemplateQuery(e.target.value)}
                  placeholder={templateSearchFocused ? '' : t('Search prompts…')}
                  className="h-full text-[11px] rounded-lg border-0 shadow-none bg-sidebar-accent/40 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 py-0 text-center leading-[46px]"
                  onFocus={() => setTemplateSearchFocused(true)}
                  onBlur={() => setTemplateSearchFocused(false)}
                  onKeyDown={(e) => {
                    // Keep typing behavior inside dropdown.
                    e.stopPropagation();
                  }}
                />
              </div>

              <DropdownMenuSeparator />

              {!String(debouncedTemplateQuery || '').trim() ? (
                <div className="h-[46px] px-3 flex items-center text-sm text-muted-foreground">
                  {t('Keep typing to search prompts…')}
                </div>
              ) : templateLoading ? (
                <div className="h-[46px] px-3 flex items-center text-sm text-muted-foreground">
                  {t('Loading…')}
                </div>
              ) : !templateMyItems.length && !templatePublicItems.length ? (
                <div className="h-[46px] p-3 flex items-center text-sm text-muted-foreground">
                  {t('No matching prompts')}
                </div>
              ) : (
                <div className="max-h-[280px] overflow-y-auto custom-scrollbar">
                  {templateMyItems.length ? (
                    <div className="p-1">
                      <div className="px-2 py-1.5 text-[10px] font-medium text-muted-foreground">
                        {t('My prompts')}
                      </div>
                      {templateMyItems.map((it) => (
                        <DropdownMenuItem
                          key={`my-${it.id}`}
                          className="rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer"
                          onClick={() => handleSelectPromptAsset(it)}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-[11px] font-medium">{it.title}</div>
                            {it.description ? (
                              <div className="truncate text-[10px] text-muted-foreground">
                                {it.description}
                              </div>
                            ) : null}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ) : null}

                  {templateMyItems.length && templatePublicItems.length ? <DropdownMenuSeparator /> : null}

                  {templatePublicItems.length ? (
                    <div className="p-1">
                      <div className="px-2 py-1.5 text-[10px] font-medium text-muted-foreground">
                        {t('Public prompts')}
                      </div>
                      {templatePublicItems.map((it) => (
                        <DropdownMenuItem
                          key={`pub-${it.id}`}
                          className="rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer"
                          onClick={() => handleSelectPromptAsset(it)}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-[11px] font-medium">{it.title}</div>
                            {it.description ? (
                              <div className="truncate text-[10px] text-muted-foreground">
                                {it.description}
                              </div>
                            ) : null}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {/* 编辑 */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-[96px] px-2.5 text-[11px] rounded-lg justify-center gap-1.5 hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group"
          onClick={() => {
            if (!canEdit) return;
            onOpenEditor();
          }}
          disabled={isToolbarBlocked || !canEdit}
        >
          <Pencil size={14} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="truncate">{t('Edit')}</span>
        </Button>

        {/* 复制 */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-[96px] px-2.5 text-[11px] rounded-lg justify-center gap-1.5 hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group"
          onClick={handleCopy}
          disabled={!text || isToolbarBlocked}
        >
          <Copy size={14} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="truncate">{t('Copy')}</span>
        </Button>

        {/* 粘贴 */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-[96px] px-2.5 text-[11px] rounded-lg justify-center gap-1.5 hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group"
          onClick={handlePasteAction}
          disabled={isToolbarBlocked}
        >
          <ClipboardPaste size={14} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="truncate">{t('Paste')}</span>
        </Button>

        {/* 清空 */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-[96px] px-2.5 text-[11px] rounded-lg justify-center gap-1.5 hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group"
          onClick={handleClearAction}
          disabled={!text || isToolbarBlocked}
        >
          <Trash2 size={14} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity text-red-500/80" />
          <span className="truncate">{t('Clear')}</span>
        </Button>

        {/* 拆分 */}
        <div className="flex items-center gap-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-[96px] px-2.5 text-[11px] rounded-l-lg rounded-r-none border-none hover:bg-sidebar-accent transition-all font-medium text-foreground/80 hover:text-foreground group gap-1.5 justify-center"
            onClick={() => onSplit?.('paragraph')}
            disabled={!text || isToolbarBlocked}
          >
            <Scissors size={14} className="shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" />
            <span className="truncate">{t('Split')}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-7 px-0 rounded-r-lg rounded-l-none border-none hover:bg-sidebar-accent transition-all"
                disabled={!text || isToolbarBlocked}
              >
                <ChevronDown size={12} className="opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border/50 shadow-2xl rounded-xl min-w-[140px] p-1.5 z-[200]">
              <DropdownMenuItem
                className="rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-[11px] font-medium"
                onClick={() => onSplit?.('paragraph')}
              >
                {t('Split by paragraph')}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-[11px] font-medium"
                onClick={() => onSplit?.('line')}
              >
                {t('Split by line')}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-[11px] font-medium"
                onClick={() => onSplit?.('sentence')}
              >
                {t('Split by sentence')}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-[11px] font-medium"
                onClick={() => onSplit?.('comma')}
              >
                {t('Split by comma')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );

  return (
    <NodeToolbarRaw
      isVisible={topToolbarShown}
      position={Position.Top}
      align="center"
      className={`fm-node-toolbar-top z-[120] -translate-y-3 md:-translate-y-4 flex items-center gap-0 rounded-none bg-transparent p-0 shadow-none transition-opacity ${topToolbarPointerEnabled ? '' : 'pointer-events-none'}`}
    >
      {toolbarButtons}
    </NodeToolbarRaw>
  );
}
